'use client';

/**
 * Shared hast → React renderer for lowlight output (the hast-emitting core of
 * rehype-highlight). Extracted from HighlightedJson (#2138) so the artifact
 * code viewer can reuse the exact same rendering — no dangerouslySetInnerHTML
 * anywhere in the highlight path.
 *
 * Token colors come from `code-highlight.css` (scoped under `.meridian-hljs`
 * on a surrounding element), light + dark.
 */

import React from 'react';

/**
 * Minimal structural view of the hast nodes lowlight emits. Narrowed at
 * runtime (type guards, no assertions) so this file has no compile-time
 * dependency on @types/hast.
 */
interface HastTextNode {
  type: 'text';
  value: string;
}

interface HastElementNode {
  type: 'element';
  children: unknown[];
  properties: Record<string, unknown>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function isTextNode(node: unknown): node is HastTextNode {
  return isRecord(node) && node.type === 'text' && typeof node.value === 'string';
}

function isElementNode(node: unknown): node is HastElementNode {
  return (
    isRecord(node) &&
    node.type === 'element' &&
    Array.isArray(node.children) &&
    isRecord(node.properties)
  );
}

/** hast `className` property → space-joined class string. */
function classNameFrom(properties: Record<string, unknown>): string | undefined {
  const value = properties.className;
  if (typeof value === 'string') return value;
  if (Array.isArray(value)) {
    const parts = value.filter(
      (item): item is string | number => typeof item === 'string' || typeof item === 'number'
    );
    if (parts.length > 0) return parts.join(' ');
  }
  return undefined;
}

export function renderHastNodes(nodes: unknown[], keyPrefix: string): React.ReactNode[] {
  return nodes.map((node, index) => {
    const key = `${keyPrefix}-${index}`;
    if (isTextNode(node)) {
      return <React.Fragment key={key}>{node.value}</React.Fragment>;
    }
    if (isElementNode(node)) {
      return (
        <span key={key} className={classNameFrom(node.properties)}>
          {renderHastNodes(node.children, key)}
        </span>
      );
    }
    // Comments/doctypes never appear in lowlight output; ignore defensively.
    return null;
  });
}
